import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Users, ArrowLeft, Plus, Trash2, Shield, Mail, Clock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminUsers = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [showInvite, setShowInvite] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch(`${API}/api/auth/me`, { credentials: 'include' })
      .then(r => { if (!r.ok) navigate('/admin/login'); })
      .catch(() => navigate('/admin/login'));
    loadUsers();
  }, [navigate]);

  const loadUsers = () => {
    fetch(`${API}/api/admin/users`, { credentials: 'include' })
      .then(r => r.json())
      .then(data => { setUsers(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    setError('');
    try {
      const res = await fetch(`${API}/api/admin/users/invite`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: inviteEmail.trim().toLowerCase(), name: inviteName.trim() }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.detail || 'Failed to invite'); return; }
      setInviteEmail(''); setInviteName(''); setShowInvite(false);
      loadUsers();
    } catch { setError('Network error'); }
  };

  const handleRemove = async (userId) => {
    if (!window.confirm('Remove this admin user?')) return;
    try {
      const res = await fetch(`${API}/api/admin/users/${userId}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (res.ok) loadUsers();
      else {
        const d = await res.json();
        setError(d.detail || 'Failed to remove');
      }
    } catch { setError('Network error'); }
  };

  return (
    <div className="min-h-screen bg-gray-100" data-testid="admin-users-page">
      <nav className="bg-black text-white shadow-lg">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center gap-3">
          <Link to="/admin" className="text-gray-400 hover:text-white"><ArrowLeft className="h-5 w-5" /></Link>
          <Shield className="h-5 w-5 text-red-500" />
          <span className="font-bold">Admin Users</span>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-black">Admin Users</h1>
            <p className="text-sm text-gray-500">{users.length} user{users.length !== 1 ? 's' : ''} with admin access</p>
          </div>
          <Button onClick={() => setShowInvite(!showInvite)} className="bg-black hover:bg-gray-800 text-white gap-2" data-testid="invite-btn">
            <Plus className="h-4 w-4" /> Add Admin
          </Button>
        </div>

        {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">{error}</div>}

        {showInvite && (
          <Card className="p-5 mb-6 border-2 border-black" data-testid="invite-form">
            <h3 className="font-bold text-sm mb-3">Pre-authorize Admin Email</h3>
            <p className="text-xs text-gray-500 mb-4">Add an email address to grant admin access. When this person signs in with Google, they'll automatically have admin privileges.</p>
            <div className="space-y-3">
              <input type="text" placeholder="Name (optional)" value={inviteName} onChange={e => setInviteName(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" data-testid="invite-name" />
              <input type="email" placeholder="Email address" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" data-testid="invite-email" />
              <div className="flex gap-2">
                <Button onClick={handleInvite} className="bg-black hover:bg-gray-800 text-white text-sm" data-testid="invite-submit">Add User</Button>
                <Button onClick={() => { setShowInvite(false); setError(''); }} variant="outline" className="text-sm">Cancel</Button>
              </div>
            </div>
          </Card>
        )}

        {loading ? (
          <div className="text-center py-12"><div className="animate-spin h-8 w-8 border-2 border-black border-t-transparent rounded-full mx-auto" /></div>
        ) : (
          <div className="space-y-3">
            {users.map(u => (
              <Card key={u.user_id} className="p-4 border-gray-200 flex items-center justify-between" data-testid={`user-${u.user_id}`}>
                <div className="flex items-center gap-3">
                  {u.picture ? (
                    <img src={u.picture} alt={u.name} className="h-10 w-10 rounded-full" />
                  ) : (
                    <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                      <Users className="h-5 w-5 text-gray-500" />
                    </div>
                  )}
                  <div>
                    <p className="font-bold text-sm text-black">{u.name || 'Invited User'}</p>
                    <p className="text-xs text-gray-500 flex items-center gap-1"><Mail className="h-3 w-3" /> {u.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right hidden sm:block">
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${u.role === 'admin' ? 'bg-black text-white' : 'bg-gray-200 text-gray-600'}`}>
                      {u.role || 'admin'}
                    </span>
                    <p className="text-[10px] text-gray-400 mt-1 flex items-center gap-1 justify-end">
                      <Clock className="h-2.5 w-2.5" /> {u.last_login ? new Date(u.last_login).toLocaleDateString() : 'Never signed in'}
                    </p>
                  </div>
                  <button onClick={() => handleRemove(u.user_id)} className="text-gray-400 hover:text-red-600 transition-colors" data-testid={`remove-${u.user_id}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUsers;
